import { debounce } from 'lodash';
import Image from 'next/image';
import { FunctionComponent, useEffect } from 'react';

import useIsMounted from '@/hooks/useIsMounted';
import { CatalogueItem } from '@/services/RewindApi';

interface TopContentProps extends RewindSlideProps {
  topPercentile: number;
  promoItems: CatalogueItem[];
}

let animationTimeouts: ReturnType<typeof setTimeout>[] = [];

const TopPercent: FunctionComponent<TopContentProps> = (props) => {
  const { topPercentile, promoItems, active = true } = props;
  const isMounted = useIsMounted();

  /* eslint-disable no-param-reassign */
  useEffect(() => {
    const clearAllTimeouts = (): boolean => {
      if (animationTimeouts.length) {
        animationTimeouts.forEach((timeoutHandler) => {
          clearTimeout(timeoutHandler);
        });

        animationTimeouts = [];

        return true;
      }

      return false;
    };
    const container = document.getElementById('top-percent-container');
    const images = document.getElementsByClassName('top-percent-img') as HTMLCollectionOf<HTMLImageElement>;

    if (
      (process.env.NODE_ENV !== 'development' || isMounted())
      && container
      && images
    ) {
      const gap = 20;
      const initialMargin = -100;
      const stackImageOffset = 100;
      const animationStep1Duration = 1000;
      const animationStep2Duration = 500;
      const mobileTitleStackSpacing = 30;
      const title = document.getElementById('top-percent-title') as HTMLDivElement;

      const getContainerPaddingLeft = () => {
        const containerStyles = getComputedStyle(container);
        return parseInt(containerStyles.paddingLeft, 10);
      };

      const getContainerPaddingRight = () => {
        const containerStyles = getComputedStyle(container);
        return parseInt(containerStyles.paddingRight, 10);
      };

      const getAvailableWidth = () => {
        return container.clientWidth - getContainerPaddingLeft() - getContainerPaddingRight();
      };

      const getImageStackOffset = (image: HTMLImageElement) => {
        return Math.ceil(image.clientHeight / 5.17);
      };

      const initialMobileStackHeight = images[0].clientHeight + ((images.length - 1) * stackImageOffset);
      const finalStackWidth = images[0].clientWidth + ((images.length - 1) * getImageStackOffset(images[0]));
      const finalStackHeight = images[0].clientHeight + ((images.length - 1) * getImageStackOffset(images[0]));

      const getRowCenteringMargin = (image: HTMLImageElement) => {
        return getContainerPaddingLeft() + (getAvailableWidth() - (images.length * image.clientWidth) - ((images.length - 1) * gap)) / 2;
      };

      const getLeftJustifyMargin = () => {
        return getContainerPaddingLeft();
      };

      const getMobileTopMargin = () => {
        const containerBoundingBox = container.getBoundingClientRect();
        return (container.clientHeight - containerBoundingBox.top - finalStackHeight - mobileTitleStackSpacing - title.clientHeight) / 2;
      };

      const setInitialStyles = (image: HTMLImageElement, index: number, setTransitionDelays = true) => {
        image.style.opacity = '0';

        if (setTransitionDelays) {
          image.style.transitionDelay = `${index * 1000}ms`;
          image.style.transitionDuration = `${animationStep1Duration}ms`;
        } else {
          image.style.transitionDelay = '';
          image.style.transitionDuration = '';
        }

        if (window.innerWidth >= 1024) {
          image.style.top = `${((container.clientHeight - image.clientHeight) / 2) + initialMargin}px`;
          image.style.left = `${getRowCenteringMargin(image) + (index * (image.clientWidth + gap))}px`;

          if (title) {
            title.style.opacity = '0';
            title.style.top = '50%';
            title.style.transform = 'translateY(-50%) translateY(100px)';
            title.style.width = `${finalStackWidth}px`;
            title.style.marginLeft = '';
            title.style.marginRight = '';
            title.style.left = '';
            title.style.right = `${getContainerPaddingRight()}px`;
          }
        } else {
          const containerBoundingBox = container.getBoundingClientRect();
          image.style.top = `${(((container.clientHeight - containerBoundingBox.top) - initialMobileStackHeight) / 2) - stackImageOffset}px`;
          image.style.left = '50%';
          image.style.transform = `translateX(-50%) translateX(${index % 2 ? 70 : -70}px)`;

          if (title) {
            title.style.opacity = '0';
            title.style.top = `${getMobileTopMargin()}px`;
            title.style.width = '';
            title.style.marginLeft = '';
            title.style.marginRight = '';
            title.style.left = '50%';
            title.style.transform = 'translateX(-50%) translateX(100px)';
            title.style.right = '';
          }
        }
      };

      const revealImages = (image: HTMLImageElement, index: number) => {
        image.style.opacity = '1';

        if (window.innerWidth >= 1024) {
          image.style.top = `${(container.clientHeight - image.clientHeight) / 2}px`;
        } else {
          const containerBoundingBox = container.getBoundingClientRect();
          image.style.top = `${(((container.clientHeight - containerBoundingBox.top) - initialMobileStackHeight) / 2) + (index * stackImageOffset)}px`;
        }
      };

      const prepStackingTransition = (image: HTMLImageElement) => {
        image.style.transitionDelay = '0ms';
        image.style.transitionDuration = `${animationStep2Duration}ms`;
      };

      const stackImages = (image: HTMLImageElement, index: number) => {
        if (window.innerWidth >= 1024) {
          image.style.left = `${getLeftJustifyMargin() + (index * getImageStackOffset(image))}px`;
          image.style.transform = '';
          image.style.top = `${((container.clientHeight - image.clientHeight) / 2) + (-getImageStackOffset(image) + (index * getImageStackOffset(image)))}px`;
        } else {
          image.style.left = '50%';
          image.style.transform = `translateX(-50%) translateX(${(index - 1) * getImageStackOffset(image)}px)`;
          image.style.top = `${getMobileTopMargin() + title.clientHeight + mobileTitleStackSpacing + (index * getImageStackOffset(image))}px`;
        }
      };

      const revealTitle = () => {
        if (title) {
          title.style.opacity = '1';

          if (window.innerWidth >= 1024) {
            title.style.width = `${finalStackWidth}px`;
            title.style.marginLeft = '';
            title.style.marginRight = '';
            title.style.top = '50%';
            title.style.transform = 'translateY(-50%)';
            title.style.left = '';
            title.style.right = `${getContainerPaddingRight()}px`;
          } else {
            title.style.width = '';
            title.style.marginLeft = 'auto';
            title.style.marginRight = 'auto';
            title.style.top = `${getMobileTopMargin()}px`;
            title.style.left = '50%';
            title.style.transform = 'translateX(-50%)';
            title.style.right = '';
          }
        }
      };

      const resizeHandler = debounce(() => {
        Array.from(images).forEach((image, index) => {
          image.style.transitionDelay = '0ms';
          image.style.transitionDuration = '0ms';
          stackImages(image, index);
          revealTitle();
        });
      }, 500);

      if (active) {
        const hasBeenCleared = clearAllTimeouts();

        Array.from(images).forEach((image, index) => {
          // If there were previous timeouts, this means there was some page exit animations.
          // To make sure we perform the page enter animations correctly, we want to first reset animated elements to their
          // initial position as fast as possible.
          if (hasBeenCleared) {
            setInitialStyles(image, index, false);
          }

          // This might be calling it a second time to reset the transition config prior to animation
          setInitialStyles(image, index, true);
          animationTimeouts.push(setTimeout(() => { revealImages(image, index); }, 1000));
          animationTimeouts.push(setTimeout(() => { prepStackingTransition(image); }, animationStep1Duration + 100));
          animationTimeouts.push(setTimeout(() => { stackImages(image, index); }, 4500));
          animationTimeouts.push(setTimeout(() => { revealTitle(); }, 5000));
        });
        window.addEventListener('resize', resizeHandler);
      } else {
        window.removeEventListener('resize', resizeHandler);
        Array.from(images).forEach((image, index) => {
          setInitialStyles(image, index, false);
        });
      }
    }
  }, [isMounted, active]);
  /* eslint-enable no-param-reassign */

  return (
    <div id="top-percent-container" className="relative w-full h-full px-2 sm:px-10 max-w-[100vh] lg:max-w-[1280px]">
      <div>
        {promoItems.map((item, index) => {
          return (
            <Image
              key={item.imageURL}
              id={`top-percent-img-${index}`}
              className="top-percent-img !h-[250px] !w-[167px] sm:!h-[345px] sm:!w-[230px] lg:!w-[266px] lg:!h-[400px] xl:!w-[345px] xl:!h-[517px]"
              src={`${item.imageURL}?crop=true&width=600&height=900&type=webp`}
              alt={item.name}
              width={600}
              height={900}
            />
          );
        })}
      </div>

      <div id="top-percent-title" className="top-percent-title w-[90%] sm:w-auto">
        <h2 className="text-xl sm:text-2xl lg:text-3xl xl:text-4xl text-left">
          You are in the
          {' '}
          <span className="text-dark-tangerine">
            top
            {' '}
            {topPercentile}
            %
          </span>
          {' '}
          of all SBS On Demand fans.
        </h2>
        <div className="text-sm md:text-base lg:text-lg text-left border-l-dark-tangerine border-l-2 pl-4 mt-[10px] sm:mt-[30px] lg:mt-[44px]">
          <p>Based on how much you&apos;ve watched this year.</p>
          <p>Keep this in mind as you start streaming in 2024!</p>
        </div>
      </div>
    </div>
  );
};

export default TopPercent;
